import { cn } from "@/lib/utils";
import React from "react";

interface LiquidGlassProps {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  borderRadius?: string;
  blurIntensity?: 'sm' | 'md' | 'lg' | 'xl';
  glowIntensity?: 'none' | 'xs' | 'sm';
  shadowIntensity?: 'none' | 'xs' | 'sm';
}

const blurClasses = {
  sm: 'backdrop-blur-sm',
  md: 'backdrop-blur-md',
  lg: 'backdrop-blur-lg',
  xl: 'backdrop-blur-xl',
};

// Premium subtle edge highlights - very fine and elegant
const shadowStyles = {
  none: 'none',
  xs: 'inset 0.5px 0.5px 1px 0 rgba(255, 255, 255, 0.15), inset -0.5px -0.5px 1px 0 rgba(255, 255, 255, 0.1)',
  sm: 'inset 1px 1px 2px 0 rgba(255, 255, 255, 0.2), inset -1px -1px 2px 0 rgba(255, 255, 255, 0.15)',
};

// Premium subtle glow - almost imperceptible but present
const glowStyles = {
  none: 'none',
  xs: '0 2px 8px rgba(0, 0, 0, 0.06), 0 0 1px rgba(255, 255, 255, 0.1)',
  sm: '0 4px 12px rgba(0, 0, 0, 0.08), 0 0 2px rgba(255, 255, 255, 0.12)',
};

export function LiquidGlass({
  children,
  className = '',
  style,
  borderRadius = '20px',
  blurIntensity = 'xl',
  glowIntensity = 'xs',
  shadowIntensity = 'xs',
}: LiquidGlassProps) {
  const layerStyle: React.CSSProperties = {
    borderRadius,
  };

  return (
    <div 
      className={cn("relative overflow-hidden", className)} 
      style={{ borderRadius, ...style }}
    >
      {/* Bend Layer (Backdrop blur) - clipped with same radius */}
      <div 
        className={cn("absolute inset-0 pointer-events-none", blurClasses[blurIntensity])} 
        style={layerStyle}
      />

      {/* Reflection Layer (Subtle glass sheen) - clipped with same radius */}
      <div 
        className="absolute inset-0 pointer-events-none" 
        style={{ 
          ...layerStyle,
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.02) 50%, rgba(255, 255, 255, 0) 100%)',
        }} 
      />

      {/* Face Layer (Main shadow and glow) - clipped with same radius */}
      <div 
        className="absolute inset-0 pointer-events-none" 
        style={{ 
          ...layerStyle,
          boxShadow: glowStyles[glowIntensity],
        }} 
      />

      {/* Edge Layer (Inner highlights) - clipped with same radius */}
      <div 
        className="absolute inset-0 pointer-events-none" 
        style={{ 
          ...layerStyle,
          boxShadow: shadowStyles[shadowIntensity],
        }} 
      />

      {/* Content */}
      <div className="relative z-10">
        {children}
      </div>
    </div>
  );
}
